import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Plus, Calendar as CalendarIcon, Wrench, Users, FileText, CheckSquare, Trash2 } from "lucide-react";
import api from "../lib/api";
import { colorFor, hexToRgba } from "../lib/colors";
import Modal from "../components/Modal";
import DateTimePicker from "../components/DateTimePicker";
import MeetingForm from "../components/MeetingForm";
import VoiceTextarea from "../components/VoiceTextarea";
import PhaseChip from "../components/PhaseChip";
import { useAuth } from "../context/AuthContext";
import { useBranding } from "../context/TenantBrandingContext";

function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function startOfGrid(d) {
  const s = startOfMonth(d);
  s.setDate(s.getDate() - s.getDay()); // start on Sunday
  return s;
}
function startOfWeek(d) {
  const s = new Date(d);
  s.setDate(d.getDate() - d.getDay());
  s.setHours(0, 0, 0, 0);
  return s;
}

const meetingEmpty = {
  title: "",
  description: "",
  customer_id: "",
  start: "",
  end: "",
  attendees: "",
};

const entryEmpty = { kind: "event", title: "", description: "", time: "" };

export default function CalendarPage() {
  const { user } = useAuth();
  const { branding } = useBranding();
  const BRAND_NAME = branding?.app_name || "Rogue";
  const [view, setView] = useState("month"); // month | week
  const [cursor, setCursor] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [entries, setEntries] = useState([]);
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [activeTechs, setActiveTechs] = useState({});
  // Iter 418.p (Dirk, 2026-02-28) — filter by event kind. All four
  // toggle-on by default. When any subset is off, the day cells +
  // week grid hide those event kinds.
  const [activeKinds, setActiveKinds] = useState({ jobcard: true, meeting: true, task: true, event: true });
  const [mOpen, setMOpen] = useState(false);
  const [mForm, setMForm] = useState(meetingEmpty);
  const navigate = useNavigate();
  const [day, setDay] = useState(null);
  const [entryOpen, setEntryOpen] = useState(false);
  const [entryForm, setEntryForm] = useState(entryEmpty);
  const [entryDate, setEntryDate] = useState(null);
  // Wave 8 — drag-to-reschedule
  const [reschedule, setReschedule] = useState(null); // { jobId, fromIso, toIso, title }

  const load = async () => {
    const [e, en, u, c] = await Promise.all([
      api.get("/calendar/events"),
      api.get("/calendar/entries"),
      api.get("/users"),
      api.get("/customers"),
    ]);
    setEvents(Array.isArray(e.data) ? e.data : []);
    setEntries(Array.isArray(en.data) ? en.data : []);
    setUsers(Array.isArray(u.data) ? u.data : []);
    setCustomers(Array.isArray(c.data) ? c.data : []);
  };
  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const init = {};
    users.forEach((u) => (init[u.id] = true));
    setActiveTechs(init);
  }, [users.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const days = useMemo(() => {
    if (view === "month") {
      const start = startOfGrid(cursor);
      return Array.from({ length: 42 }, (_, i) => {
        const d = new Date(start);
        d.setDate(start.getDate() + i);
        return d;
      });
    }
    const start = startOfWeek(cursor);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  }, [cursor, view]);

  const eventsByDay = useMemo(() => {
    const map = {};
    const add = (d, item) => {
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (!map[key]) map[key] = [];
      map[key].push(item);
    };
    // Iter 418.p — kind filter guard.
    const kindOk = (k) => !!activeKinds[k];
    // Iter 418.o (Dirk, 2026-02-28) — stretch multi-day JCs across
    // every day in their [start, end] range instead of only pinning
    // them to the start date. Meetings, lead follow-ups and other
    // single-point events stay on their start date only. Add a
    // `_span` marker so the day cell can render "day 2 of 4" style
    // continuation hints (used by the tile below).
    const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
    for (const ev of events) {
      const evKind = ev.kind || "jobcard";
      if (!kindOk(evKind)) continue;
      const start = new Date(ev.start);
      if (!Number.isFinite(start.getTime())) continue;
      const isMultiDayCandidate = ev.kind === "jobcard" && ev.end;
      if (!isMultiDayCandidate) {
        add(start, { ...ev, _kind: ev.kind || "jobcard" });
        continue;
      }
      const end = new Date(ev.end);
      if (!Number.isFinite(end.getTime()) || end <= start) {
        add(start, { ...ev, _kind: "jobcard" });
        continue;
      }
      // Walk every day between start and end (inclusive).
      const s0 = startOfDay(start);
      const e0 = startOfDay(end);
      const totalDays = Math.round((e0 - s0) / 86400000) + 1;
      let dayIdx = 0;
      for (let d = new Date(s0); d <= e0; d.setDate(d.getDate() + 1)) {
        dayIdx += 1;
        add(new Date(d), {
          ...ev,
          _kind: "jobcard",
          _span: { day: dayIdx, of: totalDays, isFirst: dayIdx === 1, isLast: dayIdx === totalDays },
        });
      }
    }
    for (const en of entries) {
      if (!kindOk(en.kind)) continue;
      add(new Date(en.date), { ...en, _kind: en.kind, kind: en.kind, title: en.title, id: en.id });
    }
    // Sort each day's events chronologically (earliest first) so the day
    // modal + week grid render consistently regardless of insert order.
    const tsOf = (it) => {
      const raw = it.start || it.date || it.scheduled_at || it.due_at || it.created_at || "";
      const ms = new Date(raw).getTime();
      return Number.isFinite(ms) ? ms : Number.POSITIVE_INFINITY;
    };
    for (const k of Object.keys(map)) {
      map[k].sort((a, b) => tsOf(a) - tsOf(b));
    }
    return map;
  }, [events, entries, activeKinds]);

  const filteredEvent = (ev) => {
    if (ev._kind === "jobcard") {
      if (!ev.technician_id) return true;
      return activeTechs[ev.technician_id] !== false;
    }
    return true;
  };

  const techUsers = users.filter((u) =>
    ["technician", "manager", "admin", "owner", "rep"].includes(u.role)
    // Iter 418.p (Dirk, 2026-02-28) — hide the seed "Hidroverse Admin"
    // user from every filter chip in this page. The row still exists
    // in DB (never delete a real user) but doesn't clutter the picker.
    && !/^hidroverse\s+admin$/i.test(u.name || "")
    && (u.email || "").toLowerCase() !== "admin@hidroverse.co.za"
  );

  const submitMeeting = async (e) => {
    e.preventDefault();
    if (!mForm.start || !mForm.end) {
      alert("Please pick start and end times");
      return;
    }
    const attendees = mForm.attendees.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
    await api.post("/meetings", {
      title: mForm.title,
      description: mForm.description,
      customer_id: mForm.customer_id || null,
      start: new Date(mForm.start).toISOString(),
      end: new Date(mForm.end).toISOString(),
      attendees,
    });
    setMOpen(false);
    setMForm(meetingEmpty);
    load();
  };

  const submitEntry = async (e) => {
    e.preventDefault();
    if (!entryDate) return;
    // Combine the picked day with the chosen time (if any)
    const dt = new Date(entryDate);
    if (entryForm.time) {
      const tt = new Date(entryForm.time);
      if (!isNaN(tt.getTime())) {
        dt.setHours(tt.getHours(), tt.getMinutes(), 0, 0);
      }
    }
    await api.post("/calendar/entries", {
      kind: entryForm.kind,
      date: dt.toISOString(),
      title: entryForm.title,
      description: entryForm.description,
    });
    setEntryOpen(false);
    setEntryForm(entryEmpty);
    setEntryDate(null);
    load();
  };

  const monthLabel = view === "month"
    ? cursor.toLocaleString('en-GB', { month: "long", year: "numeric" })
    : (() => {
        const s = startOfWeek(cursor);
        const e = new Date(s); e.setDate(s.getDate() + 6);
        return `Week of ${s.toLocaleDateString('en-GB', { month: "short", day: "numeric" })} – ${e.toLocaleDateString('en-GB', { month: "short", day: "numeric", year: "numeric" })}`;
      })();

  const navStep = (delta) => {
    if (view === "month") setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + delta, 1));
    else { const d = new Date(cursor); d.setDate(d.getDate() + delta * 7); setCursor(d); }
  };

  return (
    <div className="space-y-5" data-testid="calendar-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#647087]">{BRAND_NAME} calendar</div>
          <h1 className="font-display text-2xl md:text-4xl font-bold tracking-tight mt-1 flex items-center gap-3">
            <span className="w-2 h-2 rounded-full bg-[#A78BFA] animate-pulse" />
            {monthLabel}
          </h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <div className="flex border border-white/10 rounded-md overflow-hidden">
            {["month", "week"].map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                data-testid={`cal-view-${v}`}
                className={`px-3 py-2 text-xs uppercase tracking-wider ${
                  view === v ? "bg-[#A78BFA]/15 text-[#A78BFA]" : "text-[#647087] hover:text-white"
                }`}
              >
                {v}
              </button>
            ))}
          </div>
          <button onClick={() => navStep(-1)} className="hv-btn hv-btn-ghost" data-testid="cal-prev">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button onClick={() => setCursor(new Date())} className="hv-btn hv-btn-ghost" data-testid="cal-today">
            Today
          </button>
          <button onClick={() => navStep(1)} className="hv-btn hv-btn-ghost" data-testid="cal-next">
            <ChevronRight className="w-4 h-4" />
          </button>
          <button onClick={() => setMOpen(true)} className="hv-btn hv-btn-primary" data-testid="meeting-new-btn">
            <Plus className="w-4 h-4" /> Meeting
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {/* Iter 418.p (Dirk, 2026-02-28) — explicit Select all / None
            buttons for the tech-colour chips. Individual chips (below)
            still toggle any subset. */}
        <span className="text-[10px] uppercase tracking-wider text-[#647087] mr-1">Users</span>
        <button
          type="button"
          onClick={() => {
            const next = {};
            techUsers.forEach((u) => { next[u.id] = true; });
            setActiveTechs(next);
          }}
          className="hv-btn hv-btn-ghost text-[10px] uppercase tracking-wider"
          data-testid="cal-tech-select-all"
          title="Show every user on the calendar"
        >
          Select all
        </button>
        <button
          type="button"
          onClick={() => {
            const next = {};
            techUsers.forEach((u) => { next[u.id] = false; });
            setActiveTechs(next);
          }}
          className="hv-btn hv-btn-ghost text-[10px] uppercase tracking-wider"
          data-testid="cal-tech-select-none"
          title="Hide every user on the calendar"
        >
          None
        </button>
        {techUsers.map((u) => {
          const c = colorFor(u.id);
          const on = activeTechs[u.id] !== false;
          return (
            <button
              key={u.id}
              onClick={() => setActiveTechs({ ...activeTechs, [u.id]: !on })}
              className="px-3 py-1.5 rounded-full text-xs font-medium border transition"
              style={{
                borderColor: c,
                color: on ? c : "#647087",
                background: on ? hexToRgba(c, 0.12) : "transparent",
                opacity: on ? 1 : 0.5,
              }}
              data-testid={`cal-tech-${u.id}`}
            >
              <Users className="w-3 h-3 inline mr-1" /> {u.name}
            </button>
          );
        })}
      </div>

      {/* Iter 418.p (Dirk, 2026-02-28) — kind filter chips. Task / Job
          card / Meeting / Event. Sits right below the tech row
          so both filters live together at the top of the calendar. */}
      <div className="flex flex-wrap items-center gap-2 -mt-1">
        <span className="text-[10px] uppercase tracking-wider text-[#647087] mr-1">Kinds</span>
        <button
          type="button"
          onClick={() => setActiveKinds({ jobcard: true, meeting: true, task: true, event: true })}
          className="hv-btn hv-btn-ghost text-[10px] uppercase tracking-wider"
          data-testid="cal-kind-select-all"
          title="Show every event kind"
        >
          Select all
        </button>
        <button
          type="button"
          onClick={() => setActiveKinds({ jobcard: false, meeting: false, task: false, event: false })}
          className="hv-btn hv-btn-ghost text-[10px] uppercase tracking-wider"
          data-testid="cal-kind-select-none"
          title="Hide every event kind"
        >
          None
        </button>
        {[
          { k: "jobcard", label: "Job cards", color: "#A78BFA" },
          { k: "meeting", label: "Meetings", color: "#38BDF8" },
          { k: "task",    label: "Tasks",    color: "#10B981" },
          { k: "event",   label: "Events",   color: "#F59E0B" },
        ].map((c) => {
          const on = !!activeKinds[c.k];
          return (
            <button
              key={c.k}
              type="button"
              onClick={() => setActiveKinds((s) => ({ ...s, [c.k]: !on }))}
              className="px-3 py-1.5 rounded-full text-xs font-medium border transition"
              style={{
                borderColor: c.color,
                color: on ? c.color : "#647087",
                background: on ? hexToRgba(c.color, 0.12) : "transparent",
                opacity: on ? 1 : 0.5,
              }}
              data-testid={`cal-kind-${c.k}`}
            >
              {c.label}
            </button>
          );
        })}
      </div>

      {view === "week" ? (
        <WeekTimeGrid
          cursor={cursor}
          events={events}
          entries={entries}
          users={users}
          customers={customers}
          activeTechs={activeTechs}
          activeKinds={activeKinds}
          onSlotClick={(date) => {
            setEntryDate(date);
            // Prefill time with the picked hour
            const iso = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}T${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}:00`;
            setEntryForm({ kind: "event", title: "", description: "", time: iso });
            setEntryOpen(true);
          }}
          onEventClick={(ev) => {
            // Wave 19 #110 — meetings now open the full-page viewer/editor
            // (not a modal). Other event kinds keep their day-list popup.
            if (ev._kind === "meeting") {
              navigate(`/meetings/${ev.id}`);
              return;
            }
            setDay({ date: new Date(ev.start || ev.date), events: [ev] });
          }}
        />
      ) : (
        <div className="hv-card overflow-hidden">
          <div className="grid grid-cols-7 text-xs uppercase tracking-wider text-[#647087] border-b border-white/10">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
              <div key={d} className="px-3 py-2 text-center">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {days.map((d, i) => {
              const inMonth = d.getMonth() === cursor.getMonth();
              const isToday = d.toDateString() === new Date().toDateString();
              const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
              const dayEvents = (eventsByDay[key] || []).filter(filteredEvent);
              return (
                <button
                  key={i}
                  onClick={() => setDay({ date: d, events: dayEvents })}
                  onDragOver={(e) => {
                    if (e.dataTransfer.types.includes("application/x-hv-jobcard")) {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = "move";
                    }
                  }}
                  onDrop={(e) => {
                    const raw = e.dataTransfer.getData("application/x-hv-jobcard");
                    if (!raw) return;
                    e.preventDefault();
                    try {
                      const dragged = JSON.parse(raw);
                      // Build new ISO using same time-of-day from `from`.
                      const fromDt = new Date(dragged.from);
                      const newDt = new Date(d);
                      newDt.setHours(
                        fromDt.getHours(),
                        fromDt.getMinutes(),
                        0,
                        0,
                      );
                      if (newDt.toDateString() === fromDt.toDateString()) return;
                      setReschedule({
                        jobId: dragged.id,
                        title: dragged.title,
                        fromIso: dragged.from,
                        toIso: newDt.toISOString(),
                      });
                    } catch (_e) {
                      /* ignore bad drop */
                    }
                  }}
                  className={`min-h-[110px] p-1.5 border-b border-r border-white/5 text-left flex flex-col gap-1 transition hover:bg-white/[0.03] ${
                    inMonth ? "" : "opacity-40"
                  } ${isToday ? "bg-[#1fb6c1]/5" : ""}`}
                  data-testid={`cal-day-${d.toISOString().slice(0, 10)}`}
                >
                  <div className={`text-xs font-bold ${isToday ? "text-[#1fb6c1]" : "text-[#a0aabf]"}`}>
                    {d.getDate()}
                  </div>
                  <div className="space-y-0.5 overflow-hidden">
                    {dayEvents.slice(0, 3).map((ev) => {
                      let c = "#A78BFA";
                      // Iter 418.o (Dirk, 2026-02-28) — vehicle colour
                      // takes precedence over technician colour. Every
                      // JC assigned to the same vehicle now paints in
                      // that vehicle's brand hue (blue = van 1, green
                      // = van 2, etc.). Falls back to the tech-avatar
                      // colour if the JC has no vehicle assigned.
                      if (ev._kind === "jobcard") c = ev.vehicle_color || colorFor(ev.technician_id);
                      else if (ev._kind === "note") c = "#F59E0B";
                      else if (ev._kind === "task") c = "#10B981";
                      // Iter 418.o — span hint for multi-day JCs.
                      const spanBadge = ev._span && ev._span.of > 1
                        ? `${ev._span.day}/${ev._span.of} `
                        : "";
                      const prefix = ev._kind === "meeting" ? "📅 " : ev._kind === "note" ? "📝 " : ev._kind === "task" ? "✓ " : spanBadge;
                      // Iter 418.o — inside-span days get a flatter
                      // rounded edge on the leading side to hint the
                      // event continues from the previous day.
                      const spanClass = ev._span
                        ? `${ev._span.isFirst ? "rounded-l" : "rounded-l-none"} ${ev._span.isLast ? "rounded-r" : "rounded-r-none"}`
                        : "rounded";
                      const draggable = ev._kind === "jobcard" && (!ev._span || ev._span.isFirst);
                      return (
                        <div
                          key={`${ev._kind}-${ev.id}`}
                          draggable={draggable}
                          onDragStart={
                            draggable
                              ? (e) => {
                                  e.stopPropagation();
                                  e.dataTransfer.effectAllowed = "move";
                                  e.dataTransfer.setData(
                                    "application/x-hv-jobcard",
                                    JSON.stringify({
                                      id: ev.id,
                                      title: ev.title,
                                      from: ev.start,
                                    }),
                                  );
                                }
                              : undefined
                          }
                          className={`text-[10px] truncate px-1.5 py-0.5 ${spanClass} ${
                            draggable ? "cursor-grab active:cursor-grabbing" : ""
                          }`}
                          style={{
                            background: hexToRgba(c, 0.15),
                            color: c,
                            border: `1px solid ${hexToRgba(c, 0.4)}`,
                          }}
                          data-testid={`cal-ev-${ev._kind}-${ev.id}`}
                          title={draggable ? "Drag to reschedule" : undefined}
                        >
                          {prefix}{ev.title}
                        </div>
                      );
                    })}
                    {dayEvents.length > 3 && (
                      <div className="text-[10px] text-[#647087] px-1.5">+{dayEvents.length - 3} more</div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Day modal — view + add menu (Wave 8: max-w-2xl + better mobile spacing) */}
      <Modal
        open={!!day}
        onClose={() => setDay(null)}
        title={day ? day.date.toLocaleDateString('en-GB', { weekday: "long", month: "long", day: "numeric" }) : ""}
        testId="day-modal"
        maxWidth="max-w-2xl"
      >
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => { setEntryDate(day.date); setEntryForm({ kind: "event", title: "", description: "", time: "" }); setEntryOpen(true); setDay(null); }}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid="day-add-event"
              style={{ borderColor: "#A78BFA66", color: "#A78BFA" }}
            >
              <CalendarIcon className="w-3.5 h-3.5" /> + Event
            </button>
            <button
              onClick={() => { setEntryDate(day.date); setEntryForm({ kind: "note", title: "", description: "", time: "" }); setEntryOpen(true); setDay(null); }}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid="day-add-note"
              style={{ borderColor: "#F59E0B66", color: "#F59E0B" }}
            >
              <FileText className="w-3.5 h-3.5" /> + Note
            </button>
            <Link
              to="/tasks"
              onClick={() => setDay(null)}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid="day-add-task"
              style={{ borderColor: "#10B98166", color: "#10B981" }}
            >
              <CheckSquare className="w-3.5 h-3.5" /> + Task
            </Link>
          </div>
          <div className="space-y-2 pt-2">
            {day?.events.length === 0 && <div className="text-sm text-[#647087]">No events.</div>}
            {day?.events.map((ev) => {
              let c = "#A78BFA";
              if (ev._kind === "jobcard") c = colorFor(ev.technician_id);
              else if (ev._kind === "note") c = "#F59E0B";
              else if (ev._kind === "task") c = "#10B981";
              const tech = users.find((u) => u.id === ev.technician_id);
              const cust = customers.find((cc) => cc.id === ev.customer_id);
              const link = ev._kind === "jobcard" ? `/jobcards/${ev.id}` : null;
              // Wave 19 #104 — meetings open the rich detail modal in
              // place; jobcards keep their existing route; events/notes
              // remain non-navigable cards.
              const isMeeting = ev._kind === "meeting";
              const Wrap = link ? Link : isMeeting ? "button" : "div";
              const props = link
                ? { to: link, onClick: () => setDay(null) }
                : isMeeting
                  ? {
                      type: "button",
                      onClick: (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        navigate(`/meetings/${ev.id}`);
                        setDay(null);
                      },
                      "data-testid": `day-meeting-open-${ev.id}`,
                    }
                  : {};
              // Delete is allowed for meetings (organizer/creator) and
              // calendar entries (events / notes — author only) plus any
              // owner/admin override. Tasks/jobcards have their own delete
              // pages.
              const canDelete =
                (ev._kind === "meeting" &&
                  (user?.role === "owner" ||
                    user?.role === "admin" ||
                    ev.organizer_id === user?.id ||
                    ev.created_by_id === user?.id)) ||
                ((ev._kind === "note" || ev._kind === "event") &&
                  (user?.role === "owner" ||
                    user?.role === "admin" ||
                    ev.created_by_id === user?.id));
              const onDelete = async (e) => {
                e.preventDefault();
                e.stopPropagation();
                const label = ev._kind === "meeting" ? "meeting" : ev._kind;
                if (!window.confirm(`Delete this ${label}?`)) return;
                try {
                  if (ev._kind === "meeting") {
                    await api.delete(`/meetings/${ev.id}`);
                  } else {
                    await api.delete(`/calendar/entries/${ev.id}`);
                  }
                  setDay(null);
                  await load();
                } catch (err) {
                  const msg = err?.response?.data?.detail || "Delete failed";
                  window.alert(msg);
                }
              };
              return (
                <div key={`${ev._kind}-${ev.id}`} className="relative">
                <Wrap
                  {...props}
                  className="block border rounded-md p-3 hover:opacity-80 transition"
                  style={{ borderColor: hexToRgba(c, 0.4), background: hexToRgba(c, 0.07) }}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-xs uppercase tracking-wider flex items-center gap-1" style={{ color: c }}>
                    {ev._kind === "meeting" ? <CalendarIcon className="w-3 h-3" /> :
                     ev._kind === "note" ? <FileText className="w-3 h-3" /> :
                     ev._kind === "task" ? <CheckSquare className="w-3 h-3" /> :
                     <Wrench className="w-3 h-3" />}
                    {ev._kind === "jobcard" ? (
                      <>
                        <span>Job</span>
                        <PhaseChip value={ev.phase} className="!text-[9px] !py-0 !px-1.5" />
                      </>
                    ) : (
                      <span>{ev._kind}</span>
                    )}
                    </div>
                    {canDelete && (
                      <button
                        type="button"
                        onClick={onDelete}
                        className="text-[#9aa3b5] hover:text-rose-400 transition shrink-0"
                        title={`Delete ${ev._kind}`}
                        data-testid={`cal-event-delete-${ev._kind}-${ev.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                  <div className="font-bold mt-1">{ev.title}</div>
                  {ev.description && <div className="text-xs text-[#a0aabf] mt-1 whitespace-pre-wrap">{ev.description}</div>}
                  <div className="text-xs text-[#a0aabf] mt-1">
                    {ev.start && new Date(ev.start).toLocaleTimeString('en-GB', { hour: "2-digit", minute: "2-digit" })}
                    {tech && ` · ${tech.name}`}
                    {cust && ` · ${cust.name}`}
                  </div>
                </Wrap>
                </div>
              );
            })}
          </div>
        </div>
      </Modal>

      {/* Entry create modal */}
      <Modal
        open={entryOpen}
        onClose={() => { setEntryOpen(false); setEntryDate(null); }}
        title={`Add ${entryForm.kind} · ${entryDate?.toLocaleDateString('en-GB') || ""}`}
        testId="entry-modal"
      >
        <form onSubmit={submitEntry} className="space-y-3" data-testid="entry-form">
          <div>
            <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Title *</label>
            <input
              required
              value={entryForm.title}
              onChange={(e) => setEntryForm({ ...entryForm, title: e.target.value })}
              className="hv-input mt-1"
              data-testid="entry-input-title"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Time</label>
            <DateTimePicker
              value={entryForm.time}
              onChange={(v) => setEntryForm({ ...entryForm, time: v })}
              placeholder="Pick a specific time (optional)"
              testId="entry-input-time"
              className="mt-1"
            />
            <div className="text-[10px] text-[#647087] mt-1">
              Leave empty for an all-day entry. Picking a time anchors it on the timeline.
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Description</label>
            <VoiceTextarea
              value={entryForm.description}
              onChange={(v) => setEntryForm({ ...entryForm, description: v })}
              className="mt-1 min-h-[80px]"
              context="calendar_entry"
              testIdPrefix="entry-desc"
            />
          </div>
          <button type="submit" className="hv-btn hv-btn-primary w-full" data-testid="entry-submit">
            Save {entryForm.kind}
          </button>
        </form>
      </Modal>

      {/* Meeting modal */}
      <Modal open={mOpen} onClose={() => setMOpen(false)} title="Schedule meeting" testId="meeting-modal">
        <MeetingForm
          onCreated={() => { setMOpen(false); load(); }}
          onCancel={() => setMOpen(false)}
        />
      </Modal>

      {/* Wave 8 — drag-to-reschedule confirmation */}
      <RescheduleModal
        rs={reschedule}
        onClose={() => setReschedule(null)}
        onConfirm={async (reason) => {
          try {
            const r = await api.put(`/jobcards/${reschedule.jobId}`, {
              scheduled_at: reschedule.toIso,
              reschedule_reason: reason,
            });
            setEvents((prev) =>
              prev.map((e) =>
                e.id === reschedule.jobId
                  ? { ...e, start: r.data.scheduled_at }
                  : e,
              ),
            );
          } catch (_e) {
            /* surfaced via console for now */
          } finally {
            setReschedule(null);
          }
        }}
      />
    </div>
  );
}

function RescheduleModal({ rs, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  useEffect(() => {
    if (rs) setReason("");
  }, [rs]);
  if (!rs) return null;
  const fromDate = new Date(rs.fromIso);
  const toDate = new Date(rs.toIso);
  return (
    <Modal
      open
      onClose={onClose}
      title="Reschedule job?"
      testId="reschedule-modal"
      footer={
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="hv-btn hv-btn-ghost text-xs" data-testid="reschedule-cancel">
            Cancel
          </button>
          <button
            onClick={() => onConfirm(reason)}
            className="hv-btn hv-btn-primary text-xs"
            data-testid="reschedule-confirm"
          >
            Reschedule
          </button>
        </div>
      }
    >
      <div className="space-y-3 text-sm">
        <div className="font-medium truncate">{rs.title}</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
          <div className="border border-rose-500/30 bg-rose-500/5 rounded px-2 py-1.5">
            <div className="text-[10px] uppercase tracking-wider text-rose-300">From</div>
            <div className="font-mono">{fromDate.toLocaleString('en-GB')}</div>
          </div>
          <div className="border border-emerald-500/30 bg-emerald-500/5 rounded px-2 py-1.5">
            <div className="text-[10px] uppercase tracking-wider text-emerald-300">To</div>
            <div className="font-mono">{toDate.toLocaleString('en-GB')}</div>
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-[#647087]">
            Reason for reschedule (audit trail)
          </label>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. customer requested, weather, parts delay"
            className="hv-input w-full text-sm mt-1"
            data-testid="reschedule-reason"
            autoFocus
          />
        </div>
      </div>
    </Modal>
  );
}


// ---- Week time-grid (Google Calendar style) ----
const HOUR_PX = 48; // height of one hour slot
const START_HOUR = 6; // 06:00
const END_HOUR = 22; // 22:00
const HOURS = Array.from({ length: END_HOUR - START_HOUR + 1 }, (_, i) => START_HOUR + i);

function WeekTimeGrid({ cursor, events, entries, users, customers, activeTechs, activeKinds, onSlotClick, onEventClick }) {
  const start = startOfWeek(cursor);
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });

  const dayKey = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

  // Build positioned events per day
  const eventsByDay = useMemo(() => {
    const map = {};
    days.forEach((d) => (map[dayKey(d)] = []));

    const pushIf = (d, item) => {
      const k = dayKey(d);
      if (k in map) map[k].push(item);
    };

    // Iter 418.p — respect kind filter chips from parent.
    const kindOk = (k) => !activeKinds || activeKinds[k] !== false;

    for (const ev of events) {
      const sd = new Date(ev.start);
      const ed = ev.end ? new Date(ev.end) : new Date(sd.getTime() + 60 * 60 * 1000);
      // skip job-card events for techs filtered out
      if (ev.kind !== "meeting" && ev.technician_id && activeTechs[ev.technician_id] === false) continue;
      const evKind = ev.kind || "jobcard";
      if (!kindOk(evKind)) continue;
      pushIf(sd, { ...ev, _kind: evKind, _start: sd, _end: ed });
    }
    for (const en of entries) {
      const sd = new Date(en.date);
      const ed = new Date(sd.getTime() + 60 * 60 * 1000);
      if (!kindOk(en.kind)) continue;
      pushIf(sd, { ...en, _kind: en.kind, kind: en.kind, _start: sd, _end: ed });
    }
    return map;
  }, [days, events, entries, activeTechs, activeKinds]);

  const minutesFromTop = (date) => {
    const h = date.getHours() + date.getMinutes() / 60;
    return Math.max(0, (h - START_HOUR) * HOUR_PX);
  };
  const heightFor = (sd, ed) => {
    const dur = (ed.getTime() - sd.getTime()) / (1000 * 60 * 60);
    return Math.max(24, dur * HOUR_PX - 2);
  };

  const colorFromKind = (ev) => {
    if (ev._kind === "jobcard") return colorFor(ev.technician_id);
    if (ev._kind === "note") return "#F59E0B";
    if (ev._kind === "task") return "#10B981";
    return "#A78BFA"; // meeting/event
  };

  const handleSlotClick = (d, hour) => {
    const dt = new Date(d);
    dt.setHours(hour, 0, 0, 0);
    onSlotClick(dt);
  };

  const todayStr = new Date().toDateString();
  const now = new Date();
  const showNowLine = days.some((d) => d.toDateString() === todayStr) && now.getHours() >= START_HOUR && now.getHours() <= END_HOUR;

  return (
    <div className="hv-card overflow-hidden">
      {/* Day headers */}
      <div className="grid border-b border-white/10" style={{ gridTemplateColumns: "60px repeat(7, 1fr)" }}>
        <div className="px-2 py-2 text-[10px] uppercase tracking-wider text-[#647087]">UTC{(-new Date().getTimezoneOffset() / 60 >= 0 ? "+" : "") + (-new Date().getTimezoneOffset() / 60)}</div>
        {days.map((d, i) => {
          const isToday = d.toDateString() === todayStr;
          return (
            <div
              key={i}
              className={`px-2 py-2 text-center border-l border-white/5 ${isToday ? "bg-[#1fb6c1]/5" : ""}`}
            >
              <div className="text-[10px] uppercase tracking-wider text-[#647087]">
                {d.toLocaleDateString('en-GB', { weekday: "short" })}
              </div>
              <div className={`font-display text-xl font-bold ${isToday ? "text-[#1fb6c1]" : "text-white"}`}>
                {d.getDate()}
              </div>
            </div>
          );
        })}
      </div>

      {/* Body grid with hour rails */}
      <div className="grid relative" style={{ gridTemplateColumns: "60px repeat(7, 1fr)" }}>
        {/* Hours column */}
        <div className="border-r border-white/5">
          {HOURS.map((h) => (
            <div
              key={h}
              style={{ height: HOUR_PX }}
              className="text-[10px] text-[#647087] text-right pr-2 pt-0.5 border-b border-white/5"
            >
              {h.toString().padStart(2, "0")}:00
            </div>
          ))}
        </div>

        {/* Day columns */}
        {days.map((d, di) => {
          const dayEvents = eventsByDay[dayKey(d)] || [];
          const isToday = d.toDateString() === todayStr;
          return (
            <div key={di} className={`relative border-l border-white/5 ${isToday ? "bg-[#1fb6c1]/[0.03]" : ""}`}>
              {/* Hour slot click targets + grid lines */}
              {HOURS.map((h) => (
                <button
                  key={h}
                  type="button"
                  onClick={() => handleSlotClick(d, h)}
                  data-testid={`week-slot-${dayKey(d)}-${h}`}
                  className="block w-full border-b border-white/5 hover:bg-white/[0.03] transition"
                  style={{ height: HOUR_PX }}
                  aria-label={`Add event ${d.toDateString()} ${h}:00`}
                />
              ))}

              {/* Now line */}
              {isToday && showNowLine && (
                <div
                  className="absolute left-0 right-0 z-10 pointer-events-none"
                  style={{ top: minutesFromTop(now) }}
                >
                  <div className="h-px bg-rose-400 shadow-[0_0_8px_rgba(244,63,94,0.6)]" />
                  <div className="absolute -left-1 -top-1 w-2 h-2 rounded-full bg-rose-400" />
                </div>
              )}

              {/* Events absolute positioned */}
              {dayEvents.map((ev) => {
                const c = colorFromKind(ev);
                const top = minutesFromTop(ev._start);
                const height = heightFor(ev._start, ev._end);
                const tech = users.find((u) => u.id === ev.technician_id);
                const cust = customers.find((cc) => cc.id === ev.customer_id);
                return (
                  <button
                    key={`${ev._kind}-${ev.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onEventClick(ev);
                    }}
                    data-testid={`week-event-${ev._kind}-${ev.id}`}
                    className="absolute left-1 right-1 rounded-md px-2 py-1 text-left transition hover:brightness-125 overflow-hidden"
                    style={{
                      top,
                      height,
                      background: hexToRgba(c, 0.18),
                      border: `1px solid ${hexToRgba(c, 0.5)}`,
                      borderLeft: `3px solid ${c}`,
                    }}
                  >
                    <div className="text-[10px] uppercase tracking-wider truncate" style={{ color: c }}>
                      {ev._start.toLocaleTimeString('en-GB', { hour: "2-digit", minute: "2-digit" })}
                      {height > 36 ? ` – ${ev._end.toLocaleTimeString('en-GB', { hour: "2-digit", minute: "2-digit" })}` : ""}
                    </div>
                    <div className="text-xs font-bold truncate text-white">{ev.title}</div>
                    {height > 64 && (cust || tech) && (
                      <div className="text-[10px] text-[#a0aabf] truncate">
                        {cust?.name}{cust && tech ? " · " : ""}{tech?.name}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
